import React, { useMemo, useContext } from 'react';
import { Box, Button, Card, CardContent, CircularProgress, Typography } from '@mui/material';
import { Gauge } from '@mui/x-charts';
import { translate as t } from 'hooks/useTranslations';
import { neutral, primary } from '_lex/_foundations/phxColors';
import { AppContext } from 'views/App/context';
import useCompletionsSummary from '../_data_sources/useUserCompletedAssignmentMetrics';
import { getLmsUrl } from 'data_sources/constants';

export default function AssignmentGauge() {
  const { currentOrganizationId } = useContext(AppContext);
  const { data: completionsSummary, isLoading } = useCompletionsSummary(currentOrganizationId);

  const incompletePercentage = useMemo(() => {
    if (!completionsSummary || completionsSummary.totalUsers === 0) {
      return 0;
    }
    const usersWithoutCompletions = completionsSummary.totalUsers - completionsSummary.usersWithCompletions;
    return Math.round((usersWithoutCompletions / completionsSummary.totalUsers) * 100);
  }, [completionsSummary]);

  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300 }}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant='subtitleSm'>
            {t('dashboard.training.assignmentGauge.title', '', { percentage: incompletePercentage.toString() })}
          </Typography>
          <Typography
            variant='bodySm'
            sx={{ color: neutral[500] }}
          >
            {t('dashboard.training.assignmentGauge.description')}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Gauge
              width={240}
              height={140}
              value={incompletePercentage}
              startAngle={-90}
              endAngle={90}
              innerRadius='60%'
              outerRadius='100%'
              sx={{
                '& .MuiGauge-valueArc': {
                  fill: primary[50],
                  stroke: primary[50],
                  strokeWidth: 2,
                },
                '& .MuiGauge-referenceArc': {
                  fill: primary[800],
                  stroke: primary[800],
                  strokeWidth: 0,
                },
                '& .MuiGauge-valueText': {
                  display: 'none',
                },
              }}
            />
          </Box>
          <Button
            variant='outlined'
            color='neutral'
            fullWidth
            href={getLmsUrl('/assigned-learning/learning-plans/create')}
          >
            {t('dashboard.training.assignmentGauge.cta')}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}
